import React, { useState } from 'react';
import axios from 'axios';
import { ICategory } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  TextField, 
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';

interface AddCategoryModalProps {
  open: boolean;
  onClose: () => void;
  onCategoryAdded: () => void; // Callback to refresh list after adding
  categories?: ICategory[]; // Pass in categories to allow parent category selection
}

function AddCategoryModal({ open, onClose, onCategoryAdded, categories = [] }: AddCategoryModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentCategory, setParentCategory] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter main categories (those without a parent)
  const mainCategories = categories.filter(cat => !cat.parentCategory);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/categories`, {
        name,
        description,
        parentCategory: parentCategory || null,
      });
      setMessage(`Kategori tillagd: ${response.data.name}`);
      setName('');
      setDescription('');
      setParentCategory(null);
      if (onCategoryAdded) {
        onCategoryAdded(); // Notify parent to refresh the list
      }
      // Close modal on success
      onClose();

    } catch (err: any) {
      setMessage(`Kunde inte lägga till kategori: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal is closed
  React.useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setParentCategory(null);
      setMessage(null);
      setLoading(false);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="add-category-modal-title"
      aria-describedby="add-category-modal-description"
    >
      <Box component="form" onSubmit={handleSubmit} sx={{
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
        display: 'flex', // Use flexbox for form layout
        flexDirection: 'column', // Stack form elements vertically
        gap: 2, // Add space between form elements
      }}>
        <Typography id="add-category-modal-title" variant="h6" component="h2" gutterBottom>
          Lägg till ny kategori
        </Typography>

        <TextField
          label="Namn"
          variant="outlined"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          disabled={loading}
        />
        <TextField
          label="Beskrivning"
          variant="outlined"
          size="small"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={4}
          fullWidth
          disabled={loading}
        />
        
        <FormControl fullWidth size="small">
          <InputLabel id="parent-category-label">Huvudkategori</InputLabel>
          <Select
            labelId="parent-category-label"
            id="parent-category"
            value={parentCategory || ''}
            label="Huvudkategori"
            onChange={(e) => setParentCategory(e.target.value === '' ? null : e.target.value)}
            disabled={loading}
          >
            <MenuItem value=""><em>Ingen (Skapa huvudkategori)</em></MenuItem>
            {mainCategories.map((category) => (
              <MenuItem key={category._id} value={category._id}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Lämna tomt för att skapa en huvudkategori eller välj en befintlig för att skapa en underkategori
          </FormHelperText>
        </FormControl>

        {message && <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>{message}</Typography>}

        {/* Action buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}> {/* Adjust margin top here if needed */}
            <Button onClick={onClose} color="secondary" sx={{ mr: 1 }} disabled={loading}>
                Avbryt
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading || !name}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? 'Lägger till...' : 'Lägg till'}
            </Button>
        </Box>

      </Box>
    </Modal>
  );
}

export default AddCategoryModal; 